import { type ReactNode, useEffect, useState } from 'react';
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import {
  PublicClientApplication,
  EventType,
  InteractionRequiredAuthError,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { msalConfig, loginRequest } from './msalConfig';
import { queryClient } from '@/providers/QueryProvider';
import { AuthStatusContext } from './authStatus';

/**
 * Module-level MSAL instance.
 *
 * Exported so `main.tsx` can `await msalInstance.initialize()` once before
 * React mounts. Pre-initializing means `MsalProvider` can render its
 * children eagerly (instead of returning `null` for the duration of the
 * silent-SSO probe), which lets the landing page show up immediately
 * with a spinner on the "Sign in" button while we verify the session.
 */
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Upper bound on how long the "verifying access" state (and its landing
 * overlay) can stay up. If MSAL's `handleRedirectPromise` or `ssoSilent`
 * hangs — e.g. on a very slow network — we clear the verifying state anyway
 * so the landing page becomes interactive instead of trapping the user
 * behind an indefinite overlay/spinner. If SSO resolves afterwards, the
 * normal active-account flow still signs the user in.
 */
export const VERIFY_TIMEOUT_MS = 8000;

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isVerifyingAuth, setIsVerifyingAuth] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let didFinishVerifying = false;
    let callbackId: string | null = null;

    // Idempotent: clears the verifying state exactly once, whether MSAL
    // init finished normally or the safeguard timeout fired first.
    const finishVerifying = () => {
      if (didFinishVerifying) return;
      didFinishVerifying = true;
      clearTimeout(verifyTimeoutId);
      if (isMounted) {
        setIsVerifyingAuth(false);
      }
    };

    const verifyTimeoutId = setTimeout(finishVerifying, VERIFY_TIMEOUT_MS);

    const initializeMsal = async () => {
      // Idempotent — main.tsx already awaits this before render, but
      // calling again is a safe no-op and keeps unit tests (which render
      // AuthProvider in isolation) working without extra setup.
      await msalInstance.initialize();

      // Handle redirect response (if returning from Azure AD login)
      let wasRedirectHandled = false;
      try {
        const response = await msalInstance.handleRedirectPromise();
        if (response?.account) {
          // Flush stale cache from any previous session/user
          queryClient.clear();
          msalInstance.setActiveAccount(response.account);
          wasRedirectHandled = true;
        }
      } catch (err) {
        console.error('Redirect error:', err);
      }

      // Validate cached accounts
      const accounts = msalInstance.getAllAccounts();
      const firstAccount = accounts[0];
      if (firstAccount) {
        if (!msalInstance.getActiveAccount()) {
          msalInstance.setActiveAccount(firstAccount);
        }
      } else if (!wasRedirectHandled) {
        // No cached accounts and not returning from a redirect - try ssoSilent
        // This checks if Azure AD has an active session (via cookies) and silently authenticates
        // Enables auto-login when opening new tabs while already signed in
        try {
          const ssoResponse = await msalInstance.ssoSilent(loginRequest);
          if (ssoResponse?.account) {
            msalInstance.setActiveAccount(ssoResponse.account);
          }
        } catch (ssoError) {
          // Expected to fail if no Azure AD session exists - user must sign in interactively
          if (!(ssoError instanceof InteractionRequiredAuthError)) {
            console.debug('[Auth] SSO silent failed, interactive sign-in required');
          }
        }
      }

      if (!isMounted) return;

      callbackId = msalInstance.addEventCallback((event) => {
        if (
          event.eventType === EventType.LOGIN_SUCCESS &&
          event.payload
        ) {
          const authResult = event.payload as AuthenticationResult;
          msalInstance.setActiveAccount(authResult.account);
        }
      });

      finishVerifying();

      // After successful redirect auth, navigate to the saved return URL.
      if (wasRedirectHandled) {
        const returnUrl = sessionStorage.getItem('authReturnUrl');
        if (returnUrl && returnUrl !== '/') {
          sessionStorage.removeItem('authReturnUrl');
          window.location.replace(returnUrl);
        }
      }
    };

    initializeMsal();

    return () => {
      isMounted = false;
      clearTimeout(verifyTimeoutId);
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, []);

  // Render eagerly. The `isVerifyingAuth` flag tells downstream UI
  // (LoginPage, ProtectedRoute) that we don't yet know whether the user
  // has a silent session, so they can show a spinner on the sign-in
  // affordance instead of either flashing the landing page or blanking
  // the screen.
  return (
    <MsalProvider instance={msalInstance}>
      <AuthStatusContext.Provider value={{ isVerifyingAuth }}>
        {children}
      </AuthStatusContext.Provider>
    </MsalProvider>
  );
};

export { AuthenticatedTemplate, UnauthenticatedTemplate };
